'use client'

import { useMemo, useState } from 'react'
import { useIdleTimer } from 'react-idle-timer'

import logger from 'shared/services/logger'
import Button from 'shared/timber/v1/Button'
import Icon from 'shared/timber/v1/Icon'
import Modal from 'shared/timber/v1/Modal'
import { window } from 'shared/utils/browser'
import { isString } from 'shared/utils/type-guards'

interface Props {
  disabled?: boolean
  redirectPath?: string
  timeoutMinutes?: number

  /**
   * You may want to put this component higher up in the component tree but not
   * want nested paths to be considered "idle" paths. This is a way to ignore those
   * pages.
   */
  ignoreRegex?: string
}

/**
 * IdleSessionListener
 *
 * Basic component that can be inserted into any page or layout and will start
 * listening for idle events. When the user is idle for a certain amount of time,
 * the page will redirect to
 */
export default function IdleSessionListener({
  redirectPath = '/idle',
  timeoutMinutes = 60,
  disabled = false,
  ignoreRegex,
}: Props) {
  const [showConfirmation, setShowConfirmation] = useState(false)

  const [timeout, promptBeforeIdle] = useMemo(() => {
    if (!timeoutMinutes || timeoutMinutes <= 0) {
      return [1000 * 60 * 60, 1000 * 60 * 50]
    }

    const timeout = 1000 * 60 * timeoutMinutes
    const promptBeforeIdle = timeout * 0.1

    return [timeout, promptBeforeIdle]
  }, [timeoutMinutes])

  const isDisabled = useMemo(() => {
    if (!window) return true
    if (disabled) return true
    if (
      isString(ignoreRegex) &&
      new RegExp(ignoreRegex).test(window?.location.pathname)
    ) {
      return true
    }

    return false
  }, [disabled, ignoreRegex])

  /**
   * onIdle
   *
   * Session has set idle and we're going to redirect the user to a new page that
   * doesn't consume as many resources as something like the project plan page.
   */
  const onIdle = () => {
    if (!window) return

    logger.info(`idle session expired, redirecting to: ${redirectPath}`)
    const url = new URL(redirectPath, window.location.href)
    const redirectTo = `${window.location.pathname}${window.location.search}`
    url.searchParams.set('redirect-to', redirectTo)

    // using full reload here to also force clear some browser state
    window.location.href = url.toString()
  }

  /**
   * onPrompt
   *
   * The session is about to expire and we're giving the user 10% of the idle time to
   * respond before we kick them out to a new "idle" page.
   */
  const onPrompt = () => {
    logger.info('session is about to expire, prompting user to stay active')
    setShowConfirmation(true)
  }

  /**
   * Close prompt if session was reactivated from another tab
   */
  const onActive = () => {
    setShowConfirmation(false)
  }

  const onConfirm = () => {
    logger.info('resetting idle session timer')
    activate()
    setShowConfirmation(false)
  }

  const { activate } = useIdleTimer({
    name: `idle-${window?.location.pathname}`,
    onIdle,
    onPrompt,
    onActive,
    timeout,
    promptBeforeIdle,
    startOnMount: true,
    crossTab: true,
    disabled: isDisabled,
    syncTimers: 500,
  })

  return (
    <Modal
      open={showConfirmation}
      showCloseButton={false}
      disableOutsideClick
      data-testid="idle-session-modal"
    >
      <Modal.Header>
        <Icon background="warning" icon="clock_fast_forward" />
        <Modal.Title>Your session is about to expire</Modal.Title>
        <Modal.Description>
          We haven't seen any activity from you in a while and will be closing
          your session soon. Do you want to stay active?
        </Modal.Description>
      </Modal.Header>
      <Modal.Footer>
        <Button onClick={onConfirm} fullWidth>
          I'm Still Here!
        </Button>
      </Modal.Footer>
    </Modal>
  )
}
